import { zodResolver } from "@hookform/resolvers/zod";
import { t, Trans } from "@lingui/macro";
import { ArrowRight } from "@phosphor-icons/react";
import { loginSchema } from "@reactive-resume/dto";
import { usePasswordToggle } from "@reactive-resume/hooks";
import {
  Alert,
  AlertTitle,
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useRef, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router";
import type { z } from "zod";
import { useLogin } from "@/client/services/auth";
import { useRegister } from "@/client/services/auth";
import { useFeatureFlags } from "@/client/services/feature";
import { useToast } from "@/client/hooks/use-toast";

type FormValues = z.infer<typeof loginSchema>;

export const LoginPage = () => {
  const { login, loading: loginLoading } = useLogin();
  const { register, loading: registerLoading } = useRegister();
  const { flags } = useFeatureFlags();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const formRef = useRef<HTMLFormElement>(null);
  usePasswordToggle(formRef);

  const form = useForm<FormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { identifier: "", password: "" },
  });

  const email = searchParams.get('email');
  const resumeId = searchParams.get('resumeId');
  const previewId = searchParams.get('previewId');
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasAutoTried, setHasAutoTried] = useState(false);
  let count = 1;
  // 自动注册和登录逻辑
  const handleAutoLoginOrRegister = async () => {
    if (!email || hasAutoTried) return;
    
    setIsProcessing(true);
    setHasAutoTried(true);
    const defaultPassword = "123456789";
    const username = email.split('@')[0];
      
      try {
        // 首先尝试登录
        await login({ identifier: email, password: defaultPassword });
      } catch (loginError: any) {
        console.log('Login failed, attempting registration:', loginError);
        // 获取错误消息 - 处理 API 响应格式
        let errorMessage = '';
        // 尝试从不同的错误格式中提取消息
        if (loginError?.message) {
          errorMessage = loginError.message;
        } else if (loginError?.response?.data?.message) {
          errorMessage = loginError.response.data.message;
        } else if (typeof loginError === 'string') {
          errorMessage = loginError;
        } else {
          errorMessage = loginError?.toString() || '';
        }
        // 检查是否是凭证无效错误或用户不存在
        const isInvalidCredentials = errorMessage === 'InvalidCredentials' ||
                                   errorMessage.includes('InvalidCredentials') ||
                                   errorMessage.includes('似乎并没有使用您提供的凭据的用户') ||
                                   errorMessage.includes('credentials');
        console.log('isInvalidCredentials:', isInvalidCredentials);
        
        if (isInvalidCredentials) {
          try {
            // 登录失败，尝试注册
            await register({
              name: username,
              email,
              username,
              locale: "zh-CN",
              password: defaultPassword,
            });
            // 注册成功后自动登录
            await login({ identifier: email, password: defaultPassword });
          } catch (registerError: any) {
            console.error('Auto registration failed:', registerError);
            // 获取注册错误消息 - 处理 API 响应格式
            let registerErrorMessage = '';
            
            // 尝试从不同的错误格式中提取消息
            if (registerError?.message) {
              registerErrorMessage = registerError.message;
            } else if (registerError?.response?.data?.message) {
              registerErrorMessage = registerError.response.data.message;
            } else if (typeof registerError === 'string') {
              registerErrorMessage = registerError;
            } else {
              registerErrorMessage = registerError?.toString() || '';
            }
            
            // 检查是否是用户已存在错误
            const isUserAlreadyExists = registerErrorMessage === 'UserAlreadyExists' ||
                                       registerErrorMessage.includes('UserAlreadyExists') ||
                                       registerErrorMessage.includes('用户已存在') ||
                                       registerErrorMessage.includes('already exists');
            
            if (isUserAlreadyExists) {
              // 用户已存在，尝试直接登录
              try {
                await login({ identifier: email, password: defaultPassword });
              } catch (finalLoginError) {
                console.error('Final login attempt failed:', finalLoginError);
                // 设置表单值让用户手动操作，错误会被axios拦截器处理
                form.setValue('identifier', email);
              }
            } else {
              // 其他注册错误，错误已经被axios拦截器处理并显示
              // 只需要设置表单值让用户手动操作
              form.setValue('identifier', email);
            }
          }
        } else {
          // 其他登录错误，错误已经被axios拦截器处理并显示
          // 只需要设置表单值让用户手动操作
          form.setValue('identifier', email);
        }
      } finally {
        setIsProcessing(false);
      }
    };
  useEffect(() => {
    setTimeout(handleAutoLoginOrRegister,500*count)
    count++;
  }, []); // 修复依赖数组
    
  const onSubmit = async (data: FormValues) => {
    try {
      await login(data)
    } catch (error: any) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: error?.message || error?.toString() || '',
      });
      form.setValue('password', '');
    }
  };
  // 如果 URL 中包含 resumeId，则设置登录后的重定向到该简历的构建页
  useEffect(() => {
    if (resumeId) {
      const currentRedirect = searchParams.get('redirect');
      const target = `/builder/${resumeId}`;
      if (currentRedirect !== target) {
        const next = new URLSearchParams(searchParams);
        next.set('redirect', target);
        setSearchParams(next);
      }
    } else if (previewId) {
      const currentRedirect = searchParams.get('redirect');
      const target = `/preview/${previewId}`;
      if (currentRedirect !== target) {
        const next = new URLSearchParams(searchParams);
        next.set('redirect', target);
        setSearchParams(next);
      }
    }
  }, [resumeId, previewId, searchParams, setSearchParams]);

  const loading = loginLoading || registerLoading || isProcessing;

  return (
    <div className="space-y-8">
      <Helmet>
        <title>
          {t`Sign in to your account`} - {t`Reactive Resume`}
        </title>
      </Helmet>

      <div className="space-y-1.5">
        <h2 className="text-2xl font-semibold tracking-tight">{t`Sign in to your account`}</h2>
        <h6>
          <span className="opacity-75">{t`Don't have an account?`}</span>
          <Button asChild variant="link" className="px-1.5">
            <Link to="/auth/register">
              {t({ message: "Create one now", context: "This is a link to create a new account" })}{" "}
              <ArrowRight className="ml-1" />
            </Link>
          </Button>
        </h6>
      </div>

      {flags.isEmailAuthDisabled && (
        <Alert variant="error">
          <AlertTitle>{t`Signing in via email is currently disabled by the administrator.`}</AlertTitle>
        </Alert>
      )}

      <div className={cn(flags.isEmailAuthDisabled && "pointer-events-none select-none blur-sm")}>
        <Form {...form}>
          <form
            ref={formRef}
            className="flex flex-col gap-y-4"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <FormField
              name="identifier"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Email`}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="email"
                      className="lowercase"
                      placeholder="john.doe@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t`You can also enter your username.`}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="password"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Password`}</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="password" {...field} />
                  </FormControl>
                  <FormDescription>
                    <Trans>
                      Hold <code className="text-xs font-bold">Ctrl</code> to display your password
                      temporarily.
                    </Trans>
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="mt-4 flex items-center gap-x-4">
              <Button type="submit" disabled={loading} className="flex-1">
                {t`Sign in`}
              </Button>

              <Button asChild variant="link" className="px-4">
                <Link to="/auth/forgot-password">{t`Forgot Password?`}</Link>
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};
